// The new work order form: the customer picker and quick customer, the context strip, the type
// switch (general / storage contract / estimate), storage pricing, the haul/launch dates and the
// storage seasons' included operations, adding and editing the operations on the form (addJob),
// and the work order numbers. Moved out of app.js unchanged (docs/split-plan.md, session 7b).
import { getOpCodes } from './deviceSync';
import { isEstimateMode, renderEstimateOperations, saveEstimate } from './estimates';
import { app, auth, database } from './initFirebase';
import { getTodaysDate } from './laborParts';
import { usesDockmaster } from './marinaConfig';
import { priceTotals, toCents, toDollars } from './money';
import { addNewCustomer, showWeekCounts } from './newCustomer';
import { renderWoValidationChecklist, setCurrentOpCodeData, updateOpenDataDB } from './opCodePicker';
import { mountScheduleAssist } from './scheduleAssist';
import { getCurrentUserData, waitForAuthUser } from './shellPages';
import { suppressWorkOrderAddedToast } from './storageContracts';
import { STORAGE_DEFAULTS, STORAGE_SERVICE_CATALOG, escapeHTML, fetchIdToken, generateId, getDB, idbCursorAll, idbGet, idbPut, nextInternalWorkOrderNumber, toastMessage } from './utilities';
import { createNewOpRow, refreshWorkOrderRow, statusToneSlug } from './woTable';
import { get, ref, runTransaction } from 'firebase/database';
import { getFunctions, httpsCallable } from 'firebase/functions';

let shell = null;
export function setWoFormDeps(deps) { shell = deps; }

// Work Order Creation redesign: read-only context strip mirroring the
// existing hidden customer/boat fields (single source of truth, untouched —
// see processOpCodes()). Called from every place that populates those
// fields, since they're set from several different entry points (search
// popup, single-boat auto-select, multi-boat picker, edit-existing-WO).
export function syncWoContextStrip() {
  const get = (id) => document.getElementById(id)?.value || '';

  const boatName = get('work-order-form-boat-name');
  const customerName = get('work-order-form-customer-name');
  const mooring = get('work-order-form-boat-mooring-number-slip');
  const phone = get('work-order-form-customer-phone');
  const length = get('work-order-form-boat-length');
  const engineType = get('work-order-form-boat-engine-type');
  const batteryCount = get('work-order-form-boat-battery-count');
  const boatType = get('work-order-form-boat-boat-type');

  const boatNameEl = document.getElementById('wo-context-boat-name');
  const mooringEl = document.getElementById('wo-context-mooring');
  const customerEl = document.getElementById('wo-context-customer');
  const chipsEl = document.getElementById('wo-context-chips');
  const editorsPanel = document.getElementById('wo-editors-panel');
  if (!boatNameEl || !chipsEl) return;

  const hasBoat = !!boatName;
  boatNameEl.textContent = hasBoat ? boatName : 'No boat selected';
  const changeBtn = document.getElementById('wo-context-change-btn');
  if (changeBtn) changeBtn.textContent = hasBoat ? 'Change customer / boat' : 'Select Customer';
  if (mooringEl) mooringEl.textContent = mooring ? `Mooring #${mooring}` : '';
  if (customerEl) {
    customerEl.textContent = customerName
      ? (phone ? `${customerName} · ${phone}` : customerName)
      : 'Select a customer to get started';
  }

  chipsEl.innerHTML = '';
  [
    ['Type', boatType],
    ['LOA', length ? `${length}'` : ''],
    ['Engine', engineType],
    ['Batteries', batteryCount],
    ['Mooring', mooring],
  ].forEach(([label, value]) => {
    if (!value) return;
    const chip = document.createElement('div');
    chip.className = 'wo-context-chip';
    chip.innerHTML = `<span class="wo-context-chip-label">${label}</span><span class="wo-context-chip-value">${escapeHTML(value)}</span>`;
    chipsEl.appendChild(chip);
  });

  // Collapsed by default, full stop — with no boat selected yet the raw
  // editor inputs are all blank anyway (selection itself happens through
  // "Change customer / boat" → the D1 picker, not this panel), so there's
  // nothing useful to show. Only "Edit details" (once a boat exists) opens
  // it; only reopened while the user has explicitly done that — see
  // woEditorsManuallyExpanded below.
  if (editorsPanel) editorsPanel.classList.toggle('wo-editors-collapsed', !woEditorsManuallyExpanded);
  const editBtn = document.getElementById('wo-context-edit-btn');
  if (editBtn) editBtn.classList.toggle('hidden', !hasBoat || woEditorsManuallyExpanded);
  renderWoValidationChecklist();
}

// F1 — #wo-editors-panel stays collapsed by default (see syncWoContextStrip
// above): with no boat selected its inputs are blank anyway, and selection
// itself happens through "Change customer / boat" → the D1 picker, not this
// panel. This flag is the only way it opens: "Edit details" (shown once a
// boat exists) reveals it for in-place field edits; "Done — collapse" (or
// picking a different boat) always closes it again.
let woEditorsManuallyExpanded = false;
// The form's open/reset (app.js) collapses the editors again.
export function resetWoEditorsExpanded() { woEditorsManuallyExpanded = false; }

document.getElementById('wo-context-edit-btn')?.addEventListener('click', () => {
  woEditorsManuallyExpanded = true;
  document.getElementById('wo-editors-panel')?.classList.remove('wo-editors-collapsed');
  document.getElementById('wo-context-edit-btn')?.classList.add('hidden');
});

document.getElementById('wo-editors-done-btn')?.addEventListener('click', () => {
  woEditorsManuallyExpanded = false;
  syncWoContextStrip();
});

// D1 — opens the new grouped customer/boat picker instead of the old flat
// #customerData modal. Selecting a boat writes the exact same hidden fields
// the old flow did (see selectCustomerAndBoat below), so processOpCodes(),
// addJob() and getWorkOrderNumber() all keep working unchanged.
document.getElementById('wo-context-change-btn')?.addEventListener('click', () => {
  woEditorsManuallyExpanded = false;
  openWoCustomerPicker();
});

document.getElementById('wo-customer-picker-close')?.addEventListener('click', () => {
  document.getElementById('wo-customer-picker-overlay').classList.add('hidden');
});

document.getElementById('wo-customer-picker-overlay')?.addEventListener('click', (e) => {
  if (e.target.id === 'wo-customer-picker-overlay') e.currentTarget.classList.add('hidden');
});

export function selectCustomerAndBoat(customerData, boat) {
  const customerName = customerData.Last_Name && customerData.First_Name
    ? `${customerData.Last_Name}, ${customerData.First_Name}`
    : (customerData.Last_Name || customerData.First_Name || '');

  if (boat) {
    document.getElementById('work-order-form-boat-name').value = boat.Boat_Name || '';
    document.getElementById('work-order-form-boat-id').value = boat.Boat_ID || '';
    document.getElementById('work-order-form-boat-length').value = boat.Boat_Length || '';
    document.getElementById('work-order-form-boat-beam').value = boat.Boat_Beam || '';
    document.getElementById('work-order-form-boat-bottom-paint').value = boat.Bottom_Paint_Type || '';
    document.getElementById('work-order-form-boat-bottom-color').value = boat.Bottom_Paint_Color || '';
    document.getElementById('work-order-form-boat-mast-length').value = boat.Mast_Length || '';
    document.getElementById('work-order-form-boat-boat-type').value = boat.Boat_Type || '';
    document.getElementById('work-order-form-boat-engine-count').value = boat.Number_of_Engines || '';
    document.getElementById('work-order-form-boat-engine-type').value = boat.Engine_Type || '';
    document.getElementById('work-order-form-boat-fuel-type').value = boat.Fuel_Type || '';
    document.getElementById('work-order-form-boat-battery-count').value = boat.Number_of_Batteries || '';
    document.getElementById('work-order-form-boat-combo').value = boat.Combo_Key || '';
    document.getElementById('work-order-form-boat-mooring-number-slip').value = boat.Mooring_Number || '';
    document.getElementById('work-order-form-boat-mooring-tag').value = boat.Tag_Number || '';
    document.getElementById('work-order-form-boat-mooring-weight').value = boat.Mooring_Weight || '';
    document.getElementById('work-order-form-boat-DMid').value = boat.DM_Boat || '';
  }

  document.getElementById('work-order-form-customer-id').value = customerData.Customer_ID || '';
  document.getElementById('work-order-form-customer-name').value = customerName;
  document.getElementById('work-order-form-customer-DMid').value = customerData.DM_Customer || '';
  document.getElementById('work-order-form-customer-phone').value = shell.formatPhoneNumber(customerData.Phone);
  document.getElementById('work-order-form-customer-email').value = customerData.Email || '';
  document.getElementById('work-order-form-customer-street').value = customerData.Street || '';
  document.getElementById('work-order-form-customer-city').value = customerData.City || '';
  document.getElementById('work-order-form-customer-state').value = customerData.State || '';
  document.getElementById('work-order-form-customer-zip').value = customerData.Zip || '';

  storagePrice();
  getOpCodes();
  syncWoContextStrip();

  document.getElementById('wo-customer-picker-overlay').classList.add('hidden');
}

// Requests page (roadmap 7.1): "Start work order" / "Start estimate" for a request's customer opens
// the New Work Order form here in the shell with that customer and boat already chosen.
window.addEventListener('message', async (event) => {
  if (event.origin !== window.location.origin || event.data?.type !== 'START_WORK_ORDER') return;
  const { customerId, boatId, estimate } = event.data;
  if (!customerId) return;
  try {
    await shell.createNew(estimate ? 'estimate' : 'wo', { customerId, boatId });
  } catch (err) {
    console.error('[app.js] START_WORK_ORDER failed', err);
    toastMessage('Couldn’t open the work order form.', 'red');
  }
});

let woPickerSelectedBoatId = null;

/* ---- New customer, from the work order form's picker ---- */

/** Make a customer (and a boat, if named) with the shell's addNewCustomer — DockMaster-aware like every
 *  other customer create — and return the saved record. */
async function quickCreateCustomer({ fullName, phone, email, boatName }) {
  const parts = fullName.trim().split(/\s+/);
  const set = (id, v) => { const el = document.getElementById(id); if (!el) return; if (el.tagName === 'SELECT') el.selectedIndex = 0; else el.value = v; };
  const customerId = await generateId();
  set('newCustomerId', customerId);
  set('newBoatId', await generateId());
  ['customerSalutation', 'customerState', 'paintType', 'paintColor', 'customerBoatType'].forEach(id => set(id, ''));
  ['customerStreet', 'customerCity', 'customerZip', 'customerBoatLength', 'customerBoatBeam', 'customerComboKey', 'customerMast', 'insuranceCompany', 'insurancePolicy'].forEach(id => set(id, ''));
  set('customerFirstName', parts.length > 1 ? parts.slice(0, -1).join(' ') : '');
  set('customerLastName', parts.length > 1 ? parts[parts.length - 1] : parts[0]);
  set('customerPhone', phone);
  set('customerEmail', email);
  set('customerBoatName', boatName);
  await addNewCustomer();
  return idbGet('customerData', customerId).catch(() => null);
}

function showWoNewCustomer(prefill = '') {
  const body = document.getElementById('wo-customer-picker-body');
  document.getElementById('wo-customer-picker-count').textContent = '';
  document.getElementById('wo-customer-picker-new').hidden = true;
  body.innerHTML = `<div class="wo-new-cust">
      <div class="wo-new-cust-title">New customer</div>
      <label class="wo-new-cust-field"><span class="wo-nc-label">Full name</span><input id="wo-nc-name" autocomplete="off" placeholder="Dana Whitfield" value="${escapeHTML(prefill)}"></label>
      <div class="wo-new-cust-dup" id="wo-nc-dup" hidden></div>
      <label class="wo-new-cust-field"><span class="wo-nc-label">Phone</span><input id="wo-nc-phone" autocomplete="off" placeholder="(516) 555-0187"></label>
      <label class="wo-new-cust-field"><span class="wo-nc-label">Email <i>· optional</i></span><input id="wo-nc-email" autocomplete="off" placeholder="name@email.com"></label>
      <label class="wo-new-cust-field"><span class="wo-nc-label">Boat name <i>· for this work order</i></span><input id="wo-nc-boat" autocomplete="off" placeholder="Sea Breeze"></label>
      <div class="wo-new-cust-hint">Address, company and everything else live on the customer's record. Add them there whenever.</div>
      <div class="wo-new-cust-actions">
        <button type="button" class="wo-new-cust-back" id="wo-nc-back">Back to search</button>
        <button type="button" class="wo-new-cust-create" id="wo-nc-create">Create &amp; choose</button>
      </div>
    </div>`;
  const name = document.getElementById('wo-nc-name');
  (prefill ? document.getElementById('wo-nc-phone') : name).focus();
  // Already a customer? Say so, with a one-click way to pick them instead.
  const checkDup = async () => {
    const n = name.value.trim().toLowerCase().replace(/\s+/g, ' ');
    const dup = document.getElementById('wo-nc-dup');
    if (!dup) return;
    const all = n ? await idbCursorAll('customerData').catch(() => []) : [];
    const hit = all.find(c => `${c.First_Name || ''} ${c.Last_Name || ''}`.trim().toLowerCase() === n);
    dup.hidden = !hit;
    if (hit) {
      dup.innerHTML = `Already a customer: <b>${escapeHTML([hit.First_Name, hit.Last_Name].filter(Boolean).join(' '))}</b> <button type="button">Choose them</button>`;
      dup.querySelector('button').onclick = () => backToSearch(`${hit.First_Name || ''} ${hit.Last_Name || ''}`.trim());
    }
  };
  let t = null;
  name.addEventListener('input', () => { clearTimeout(t); t = setTimeout(checkDup, 200); });
  if (prefill) checkDup();
  const backToSearch = (q = '') => {
    document.getElementById('wo-customer-picker-new').hidden = false;
    const box = document.getElementById('wo-customer-picker-search');
    box.value = q;
    box.focus();
    renderWoCustomerPickerResults(q.toLowerCase());
  };
  document.getElementById('wo-nc-back').onclick = () => backToSearch(name.value.trim());
  document.getElementById('wo-nc-create').onclick = async (e) => {
    const fullName = name.value.trim();
    const phone = document.getElementById('wo-nc-phone').value.trim();
    if (!fullName || !phone) { toastMessage('Name and phone are required', 'red', 2000); return; }
    e.target.disabled = true;
    e.target.textContent = 'Creating…';
    const boatName = document.getElementById('wo-nc-boat').value.trim();
    const customer = await quickCreateCustomer({ fullName, phone, email: document.getElementById('wo-nc-email').value.trim(), boatName });
    if (!customer) {
      toastMessage('Couldn’t create the customer. Try again.', 'red', 3000);
      e.target.disabled = false;
      e.target.textContent = 'Create & choose';
      return;
    }
    const boat = boatName ? Object.values(customer.Boats || {}).find(b => b && b.Boat_Name === boatName) || null : null;
    document.getElementById('wo-customer-picker-new').hidden = false;
    selectCustomerAndBoat(customer, boat);
  };
}

document.getElementById('wo-customer-picker-new')?.addEventListener('click', () => {
  showWoNewCustomer(document.getElementById('wo-customer-picker-search').value.trim());
});

export async function openWoCustomerPicker() {
  document.getElementById('wo-customer-picker-new').hidden = false;
  woPickerSelectedBoatId = document.getElementById('work-order-form-boat-id')?.value || null;
  document.getElementById('wo-customer-picker-search').value = '';
  document.getElementById('wo-customer-picker-overlay').classList.remove('hidden');
  await renderWoCustomerPickerResults('');
  document.getElementById('wo-customer-picker-search').focus();
}

let woPickerSearchTimer = null;
document.getElementById('wo-customer-picker-search')?.addEventListener('input', (e) => {
  clearTimeout(woPickerSearchTimer);
  const q = e.target.value.trim().toLowerCase();
  woPickerSearchTimer = setTimeout(() => renderWoCustomerPickerResults(q), 150);
});

async function renderWoCustomerPickerResults(query) {
  const body = document.getElementById('wo-customer-picker-body');
  const countEl = document.getElementById('wo-customer-picker-count');
  body.innerHTML = '';

  if (!query) {
    countEl.textContent = '';
    body.innerHTML = '<div class="wo-picker-empty">Start typing a customer name&hellip;</div>';
    return;
  }

  let customers;
  try {
    customers = await idbCursorAll('customerData');
  } catch (error) {
    console.error('Error loading customers for picker:', error);
    body.innerHTML = '<div class="wo-picker-empty">Could not load customers.</div>';
    return;
  }

  // Any order, commas or not, and boat names: "abernathy, colleen", "colleen ab", "bella luna".
  const terms = query.split(/[\s,]+/).filter(Boolean);
  const matches = customers.filter(c => {
    if (!c || c.id === 'lastUpdated') return false;
    const boats = Object.values(c.Boats || {}).map(b => b?.Boat_Name || '').join(' ');
    const hay = `${c.First_Name || ''} ${c.Last_Name || ''} ${c.Company || ''} ${boats}`.toLowerCase();
    return terms.every(t => hay.includes(t));
  }).slice(0, 20);

  countEl.textContent = matches.length ? `${matches.length} customer${matches.length === 1 ? '' : 's'}` : '';

  if (!matches.length) {
    body.innerHTML = `<div class="wo-picker-empty">No customers match.<br><button type="button" class="wo-picker-new-inline" id="wo-picker-new-inline">+ New customer “${escapeHTML(document.getElementById('wo-customer-picker-search').value.trim())}”</button></div>`;
    document.getElementById('wo-picker-new-inline').onclick = () => showWoNewCustomer(document.getElementById('wo-customer-picker-search').value.trim());
    return;
  }

  matches.forEach((customer) => {
    const name = customer.Last_Name && customer.First_Name
      ? `${customer.Last_Name}, ${customer.First_Name}`
      : (customer.Last_Name || customer.First_Name || '');
    const initials = ((customer.First_Name || '')[0] || '').toUpperCase() + ((customer.Last_Name || '')[0] || '').toUpperCase();
    const dmLinked = !!customer.DM_Customer;
    const dm = usesDockmaster();

    const group = document.createElement('div');
    group.className = 'wo-picker-group';

    const boats = customer.Boats
      ? Object.values(customer.Boats).filter(b => b && typeof b === 'object').sort((a, b) => (a.Boat_Name || '').localeCompare(b.Boat_Name || ''))
      : [];

    group.innerHTML = `
      <div class="wo-picker-group-head">
        <div class="wo-picker-group-avatar">${initials || '?'}</div>
        <div class="wo-picker-group-main">
          <div class="wo-picker-group-name">${escapeHTML(name)}</div>
          <div class="wo-picker-group-sub">${escapeHTML(shell.formatPhoneNumber(customer.Phone) || '')}</div>
        </div>
        ${dm ? `<span class="wo-picker-dm-pill ${dmLinked ? 'linked' : 'unlinked'}">${dmLinked ? 'DM linked ✓' : 'no DM ID'}</span>` : ''}
      </div>
      ${boats.map(boat => `
        <div class="wo-picker-boat-row ${boat.Boat_ID === woPickerSelectedBoatId ? 'selected' : ''}" data-boat-id="${escapeHTML(boat.Boat_ID || '')}">
          <img class="wo-picker-boat-icon" src="/assets/boat.png" width="17" height="17" alt="">
          <div class="wo-picker-boat-main">
            <div class="wo-picker-boat-name">${escapeHTML(boat.Boat_Name || '')}</div>
            <div class="wo-picker-boat-sub">${boat.Mooring_Number ? `Mooring ${escapeHTML(boat.Mooring_Number)}` : 'no mooring on file'}${boat.DM_Boat || !dm ? '' : ' &middot; no Dockmaster boat ID'}</div>
          </div>
          ${boat.Boat_ID === woPickerSelectedBoatId ? '<span class="wo-picker-selected-pill">Selected ✓</span>' : (dm && !boat.DM_Boat ? '<span class="wo-picker-warn-pill">no DM boat ID</span>' : '')}
        </div>
      `).join('') || '<div class="wo-picker-boat-row" style="padding-left:14px; cursor:default;"><div class="wo-picker-boat-sub">No boats on file for this customer.</div></div>'}
    `;

    group.querySelectorAll('.wo-picker-boat-row[data-boat-id]').forEach((row) => {
      const boat = boats.find(b => b.Boat_ID === row.dataset.boatId);
      if (boat) row.addEventListener('click', () => selectCustomerAndBoat(customer, boat));
    });

    body.appendChild(group);
  });

  if (usesDockmaster()) {
    const hint = document.createElement('div');
    hint.className = 'wo-picker-hint';
    hint.textContent = "A boat without a Dockmaster ID can still be selected — the WO number step will say exactly why it's blocked.";
    body.appendChild(hint);
  }
}

// "Create work order" in the new top bar proxies the existing #add-btn
// ("Complete") handler rather than duplicating its reset/return logic.
document.getElementById('wo-create-done-btn')?.addEventListener('click', () => {
  if (isEstimateMode()) {
    saveEstimate();
    return;
  }
  // A work order is its operations: with none added nothing has been saved, so say so instead of
  // closing as if it had been created.
  if (!document.querySelector('#activeJobs .wo-job-row')) {
    toastMessage(document.getElementById('work-order-form-customer-id')?.value
      ? 'Add an operation first. Nothing has been saved yet.'
      : 'Pick a customer and boat, then add an operation.', 'red');
    return;
  }
  document.getElementById('add-btn')?.click();
});

// Picking mode: one window, not two. The op picker (#opCodeTable) and the op form (#job) live in
// #wo-pick-pane inside the work order window (they are only ever opened from it), and while
// either is open the form shrinks to a rail with the boat, the work order's own fields and the
// Operations list, so each added op shows up beside the picker. Driven by a MutationObserver on
// each panel's style.display, so every open/close path (#selectOpCode, a code row, data-close,
// #jobExit, Edit on an added op) keeps it right.
const woPickPane = document.getElementById('wo-pick-pane');
['opCodeTable', 'job'].forEach((id) => {
  const el = document.getElementById(id);
  if (el && woPickPane) woPickPane.appendChild(el);
});

const isShown = (id) => document.getElementById(id)?.style.display === 'flex';

function syncWoPickerOpenState() {
  const picking = isShown('opCodeTable') || isShown('job');
  const modal = document.getElementById('addModal');
  const wasPicking = modal?.classList.contains('wo-picking');
  modal?.classList.toggle('wo-picking', picking);
  // A general work order keeps its Category and Short desc in the rail; the other types' fields stay behind.
  modal?.classList.toggle('wo-picking-general', picking && document.getElementById('work-order-type-select')?.value === 'general-work-order');
  woPickPane?.classList.toggle('wo-pick-detail', isShown('job'));
  if (picking && !wasPicking) renderWoPickSummary();
}

['opCodeTable', 'job'].forEach((id) => {
  const el = document.getElementById(id);
  if (el) new MutationObserver(syncWoPickerOpenState).observe(el, { attributes: true, attributeFilter: ['style'] });
});

// The rail's header: the boat and customer, and the work order number(s) once there are any.
export function renderWoPickSummary() {
  const val = (id) => document.getElementById(id)?.value || '';
  const boat = val('work-order-form-boat-name');
  const mooring = val('work-order-form-boat-mooring-number-slip');
  const customer = val('work-order-form-customer-name');
  const numbers = ['generalWorkOrder', 'winterWorkOrder', 'springWorkOrder', 'shrinkwrapWorkOrder'].map(val).filter(Boolean);
  const unique = [...new Set(numbers)];
  const boatEl = document.getElementById('wo-pick-summary-boat');
  const metaEl = document.getElementById('wo-pick-summary-meta');
  if (boatEl) boatEl.textContent = boat ? (mooring ? `${boat} · ${mooring}` : boat) : 'No boat selected';
  const numberText = isEstimateMode() ? 'Estimate # assigned on save' : (unique.join(', ') || 'WO # assigned with the first operation');
  if (metaEl) metaEl.textContent = [customer, numberText].filter(Boolean).join(' · ');
}

// Leaves picking mode: closes the op form (as its ×/Cancel would) and the picker.
function closeWoPicking() {
  if (isShown('job')) document.getElementById('jobExit')?.click();
  const table = document.getElementById('opCodeTable');
  if (table) table.style.display = 'none';
}

document.getElementById('wo-pick-done-btn')?.addEventListener('click', closeWoPicking);

// Esc backs out one step: op form → list → form.
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape' || !document.getElementById('addModal')?.classList.contains('wo-picking')) return;
  if (document.getElementById('mooringModal-mooring')?.style.display === 'block') return;
  e.preventDefault();
  if (isShown('job')) document.getElementById('jobExit')?.click();
  else closeWoPicking();
});

// Hide the global app navbar (page title + mobile menu toggle) while the
// creation modal is open — it duplicated the modal's own breadcrumb and
// didn't match the new look. Scoped to "while #Add is open" rather than
// removed outright, since .navbar is shared by every page and is the only
// way to open the nav rail on mobile.
// Closing the form also closes the picker inside it, and forgets the category a duplicated
// operation brought in (addOperation.js sets overallCategory; a new work order must not keep it).
function syncNavbarHiddenForAdd() {
  const isOpen = document.getElementById('Add')?.style.display === 'flex';
  document.body.classList.toggle('wo-add-open', !!isOpen);
  if (!isOpen) {
    ['opCodeTable', 'job'].forEach((id) => { const el = document.getElementById(id); if (el && el.style.display !== 'none') el.style.display = 'none'; });
    exitEditOperationMode();
    const overall = document.getElementById('overallCategory');
    if (overall) overall.value = '';
    delete document.getElementById('categorySelect')?.dataset.userSet;
  }
}

// Category follows the first operation (addJob) until the user picks one themselves.
document.getElementById('categorySelect')?.addEventListener('change', (e) => {
  if (e.isTrusted) e.target.dataset.userSet = '1';
});
{
  const addEl = document.getElementById('Add');
  if (addEl) new MutationObserver(syncNavbarHiddenForAdd).observe(addEl, { attributes: true, attributeFilter: ['style'] });
}

// Type segmented control drives the existing (now visually hidden)
// #work-order-type-select so its real 'change' listener — which shows/hides
// the General vs. Storage Contract sections — runs completely unmodified.
document.querySelectorAll('.wo-type-btn').forEach((btn) => {
  btn.addEventListener('click', () => {
    const select = document.getElementById('work-order-type-select');
    if (!select || select.value === btn.dataset.woType) return;
    select.value = btn.dataset.woType;
    select.dispatchEvent(new Event('change'));
    document.querySelectorAll('.wo-type-btn').forEach(b => b.classList.toggle('active', b === btn));
    renderWoValidationChecklist();
  });
});

// Set by the add-operation forms just before addJob() runs, when the office picked
// a tech in the "Schedule it now" box (scheduleAssist.js): { tech, start }. addJob()
// saves the new operation already assigned and dated instead of into the backlog.
let pendingOpSchedule = null;
// Set from app.js's Add button and the op panel's Add operation modal.
export function setPendingOpSchedule(v) { pendingOpSchedule = v; }

// Same box in the work-order creation flow's operation form (#job). Off while
// editing an existing operation or building an estimate — nothing is scheduled there.
export const jobAssist = mountScheduleAssist({
  box: document.getElementById('job-schedule-assist'),
  getDraft: () => ({
    category: (editingOperationJobBox || isEstimateMode()) ? ''
      : (document.getElementById('overallCategory')?.value || document.getElementById('category').value || ''),
    hours: document.getElementById('estimatedHours').value,
    begin: document.getElementById('begin').value,
    opType: document.getElementById('opType').value,
    description: document.getElementById('description').value,
    shortDesc: document.getElementById('dmShortDesc').value,
  }),
});
document.getElementById('estimatedHours').addEventListener('input', jobAssist.refreshSoon);
document.getElementById('begin').addEventListener('change', jobAssist.refreshSoon);
document.getElementById('category').addEventListener('change', jobAssist.refreshSoon);
document.getElementById('opType').addEventListener('change', jobAssist.refreshSoon);

export function closeAdd() {

  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;

  document.getElementById('Add').style.display = "none"
  document.getElementById('general-work-order-button').style.display = 'flex'
  document.getElementById('customerDatabaseButton').style.display = 'flex'
  document.querySelector('.work-order-type').style.display = 'flex';
  document.getElementById("generalWorkOrder").value = ""
  document.getElementById("winterWorkOrder").value = ""
  document.getElementById("springWorkOrder").value = ""
  document.getElementById("shrinkwrapWorkOrder").value = ""
  document.getElementById("work-order-form-customer-id").value = ""
  document.getElementById("work-order-form-customer-DMid").value = ""
  document.getElementById("work-order-form-boat-id").value = ""
  document.getElementById("work-order-form-boat-DMid").value = ""
  document.getElementById("work-order-form-customer-name").value = ""
  document.getElementById("work-order-form-boat-name").value = ""
  document.getElementById('work-order-form-customer-phone').value = "";
  document.getElementById("work-order-form-customer-email").value = ""
  document.getElementById("work-order-form-customer-street").value = "";
  document.getElementById("work-order-form-customer-city").value = "";
  document.getElementById("work-order-form-customer-state").value = "";
  document.getElementById("work-order-form-customer-zip").value = "";
  document.getElementById('shortDesc').value = ""
  document.getElementById('work-order-form-dates-haul-date').value = ''
  document.getElementById('work-order-form-dates-launch-date').value = nextYear + "-04-01";
  document.getElementById('work-order-form-storage-confirm').checked = false
  document.getElementById('work-order-form-boat-mooring-number-slip').value = ''
  document.getElementById('work-order-form-boat-length').value = ''
  document.getElementById('work-order-form-boat-beam').value = ''
  document.getElementById('work-order-form-boat-combo').value = ''
  document.getElementById('work-order-form-boat-mooring-tag').value = ''
  document.getElementById('work-order-form-boat-bottom-color').value = ''
  document.getElementById('work-order-form-boat-bottom-paint').value = ''
  document.getElementById('work-order-form-boat-mast-length').value = ''
  document.getElementById('work-order-form-boat-engine-count').value = ''
  document.getElementById('work-order-form-boat-battery-count').value = ''
  document.getElementById('work-order-form-storage-price').value = ''
  document.getElementById('storagePrice').innerHTML = ''
  document.getElementById('shortDesc').value = ""

  var Parent = document.getElementById('activeJobs')
  while (Parent.hasChildNodes()) {
    Parent.removeChild(Parent.firstChild)
  }

  document.getElementById('add-ops-btn').classList.add('hidden')
  document.getElementById('add-btn').classList.remove('hidden')

  let codeTable = document.getElementById('op-code-body').querySelectorAll('.op-code-row');

  for (let code of codeTable) {
    code.classList.remove('hidden')
  }

  // Scoped to #activeJobs — see the matching fix in addOpen()'s close handler
  // for why an unscoped getElementsByClassName('active') crashes here.
  const activeJobs = document.getElementById('activeJobs').getElementsByClassName('active')

  while (activeJobs.length) {

    const number = activeJobs[0].id.at(-1)

    document.getElementById("category" + number).value = ""
    document.getElementById("opType" + number).value = ""
    document.getElementById('opCode' + number).value = ""
    document.getElementById("description" + number).value = ""
    document.getElementById("date" + number).value = ""
    document.getElementById("tech" + number).value = ""
    document.getElementById("status" + number).value = ""
    document.getElementById("job" + number).classList.remove('active')
    document.getElementById("job" + number).classList.add('inactive')
    document.getElementById('addJobButton' + number).classList.remove('hidden')
    document.getElementById('deleteJobButton' + number).classList.add('hidden')
    document.getElementById('jobExit' + number).classList.remove('hidden')
    document.getElementById('jobMinimize' + number).classList.add('hidden')
  }

  var Parent = document.getElementById('activeJobs')
  while (Parent.hasChildNodes()) {
    Parent.removeChild(Parent.firstChild)
  }
}

document.getElementById('exitOpWo').addEventListener('click', () => {
  document.getElementById('openWOTable').style.display = "none"
  document.getElementById('newWorkGroup').style.display = "none"
  document.getElementById('work-order-button-move').style.display = "inline-block"
  document.getElementById('open-wo-table').style.display = "table"
})

export function AddRow() {

  document.getElementById('Add').style.display = "none"
  document.getElementById('general-work-order-button').style.display = 'flex'
  document.getElementById('customerDatabaseButton').style.display = 'flex'
  document.getElementById("work-order-form-customer-id").value = ""
  document.getElementById("work-order-form-customer-DMid").value = ""
  document.getElementById("work-order-form-boat-id").value = ""
  document.getElementById("work-order-form-boat-DMid").value = ""
  document.getElementById("work-order-form-customer-name").value = ""
  document.getElementById("work-order-form-boat-name").value = ""
  document.getElementById("generalWorkOrder").value = ""
  document.getElementById('work-order-form-customer-phone').value = "";
  document.getElementById("work-order-form-customer-email").value = "";
  document.getElementById('work-order-form-customer-street').value = "";
  document.getElementById('work-order-form-customer-city').value = "";
  document.getElementById('work-order-form-customer-state').value = "";
  document.getElementById('work-order-form-customer-zip').value = "";
  document.getElementById('shortDesc').value = "";
  document.getElementById('winterWorkOrder').value = ''
  document.getElementById('springWorkOrder').value = ''
  document.getElementById("shrinkwrapWorkOrder").value = ""
  document.getElementById('work-order-form-dates-haul-date').value = ''
  document.getElementById('work-order-form-dates-launch-date').value = (Number(new Date().getFullYear()) + 1) + '-04-01';
  document.getElementById('work-order-form-storage-confirm').checked = false
  document.getElementById('work-order-form-boat-mooring-number-slip').value = ''
  document.getElementById('work-order-form-boat-length').value = ''
  document.getElementById('work-order-form-boat-beam').value = ''
  document.getElementById('work-order-form-boat-combo').value = ''
  document.getElementById('work-order-form-boat-mooring-tag').value = ''
  document.getElementById('work-order-form-boat-bottom-color').value = ''
  document.getElementById('work-order-form-boat-bottom-paint').value = ''
  document.getElementById('work-order-form-boat-mast-length').value = ''
  document.getElementById('work-order-form-boat-engine-count').value = ''
  document.getElementById('work-order-form-boat-battery-count').value = ''
  document.getElementById('work-order-form-storage-price').value = ''
  document.getElementById('storagePrice').innerHTML = ''

  document.getElementById('exitAdd').scrollIntoView()

  var Parent = document.getElementById('activeJobs')
  while (Parent.hasChildNodes()) {
    Parent.removeChild(Parent.firstChild)
  }

}

// Phase 2 — one source of truth for storage tiers/tax/environmental fee,
// editable at Settings > Storage rates & contract (/pricing/storageContract).
// Falls back to the exact literals this function used to hardcode
// (13.35/11.35/.../.08625/.025) whenever the record hasn't loaded yet or
// has never been saved, so pricing behavior is unchanged until someone
// actually edits the new Settings tab — this phase adds the source of
// truth, it doesn't silently change what boats are charged.
export const STORAGE_PRICING_FALLBACK = {
  tiers: [
    { from: 0, to: 20, rate: 13.35 },
    { from: 20.1, to: 26, rate: 11.35 },
    { from: 26.1, to: 32, rate: 10.15 },
    { from: 32.1, to: 39, rate: 9.60 },
    { from: 39.1, to: 50, rate: 8.65 },
    { from: 50.1, to: 70, rate: 8.25 },
  ],
  taxPct: 8.625,
  envPct: 2.5,
};

export let storageContractSettings = null;

// Merges with STORAGE_DEFAULTS the same way settings.js's own editor does
// (see loadStorageSettings() there) — so storageContractSettings is never
// bare null/half-empty here. Before this merge, an un-configured Settings >
// Storage rates (this test project's actual state) left the printed
// contract's Season line and entire Terms & Conditions section blank.
async function loadStorageContractSettings() {
  try {
    const snap = await get(ref(database, '/pricing/storageContract'));
    const saved = snap.val() || {};
    storageContractSettings = {
      ...STORAGE_DEFAULTS,
      ...saved,
      dates: { ...STORAGE_DEFAULTS.dates, ...(saved.dates || {}) },
    };
  } catch (error) {
    console.error('Error loading storage contract settings:', error);
    storageContractSettings = { ...STORAGE_DEFAULTS };
  }
  storagePrice(); // refresh the price card if length/beam were already entered
}
waitForAuthUser().then(loadStorageContractSettings);

export function storageTierFor(length) {
  const tiers = (storageContractSettings?.tiers?.length) ? storageContractSettings.tiers : STORAGE_PRICING_FALLBACK.tiers;
  return tiers.find(t => length >= t.from && length <= t.to);
}

document.getElementById('work-order-form-boat-beam').addEventListener('input', () => {
  storagePrice()
})

document.getElementById('work-order-form-boat-length').addEventListener('input', () => {
  storagePrice()
})

export function storagePrice() {
  var length = Number(document.getElementById('work-order-form-boat-length').value)
  var beam = Number(document.getElementById('work-order-form-boat-beam').value)
  var tax = (storageContractSettings?.taxPct ?? STORAGE_PRICING_FALLBACK.taxPct) / 100
  var environmental = (storageContractSettings?.envPct ?? STORAGE_PRICING_FALLBACK.envPct) / 100

  var tier = storageTierFor(length)
  var rate = tier ? tier.rate : undefined
  var tierLabel = tier ? (tier.from <= 0 ? `up to ${tier.to}'` : `${tier.from}-${tier.to}'`) : ''
  var price = tier ? Number(length * beam * rate) : 0

  // money.js: tax and fee each rounded to the cent, so the lines below add up to the total.
  const priced = priceTotals({ subtotal: toCents(price), taxRate: tax, feeRate: environmental })
  var num = toDollars(priced.total)
  // toLocaleString() with no options defaults to up to 3 fraction digits
  // ("2,264.719") — force 2, both for a cleaner look and so the price card
  // doesn't force extra width in the storage rail.
  var money = function (n) { return Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
  // The G rail already labels each line ("Base price" / "Sales tax" /
  // "Environmental fee" / "Total" in the surrounding markup), so the old
  // "(Base Price)"/"(Tax)"/"(Total)" suffixes here would just duplicate
  // that label right next to it — dropped from the visible spans. The
  // hidden #work-order-form-storage-price field keeps its original format
  // since nothing but this function reads/writes it.
  document.getElementById('basePrice').innerHTML = '$' + money(toDollars(priced.subtotal))
  document.getElementById('storageTax').innerHTML = "+ $" + money(toDollars(priced.tax))
  document.getElementById('storageEnv').innerHTML = "+ $" + money(toDollars(priced.fee))
  document.getElementById('storagePrice').innerHTML = '$' + money(num)
  document.getElementById('work-order-form-storage-price').value = '$' + money(num) + "(Total)"

  // G1 rail chips — purely additive display of the same bands above; no
  // behavior change if these elements aren't present elsewhere.
  const dimsChip = document.getElementById('wo-storage-dims-chip');
  if (dimsChip) dimsChip.textContent = (length && beam) ? `${length}' × ${beam}'` : '';
  const tierChip = document.getElementById('wo-storage-tier-chip');
  if (tierChip) tierChip.textContent = rate ? `$${rate}/sq ft · ${tierLabel} tier` : '';

}

document.getElementById('jobExit').addEventListener('click', () => {
  document.getElementById('job').style.display = "none"
  const opCode = document.getElementById("opCode").value
  if (opCode) document.getElementById(opCode)?.classList.remove('hidden')
  exitEditOperationMode();
})

// F2 footer Cancel — same close behavior as the header's × (jobExit),
// just a second, more discoverable entry point next to Delete/Add.
document.getElementById('job-detail-cancel-btn')?.addEventListener('click', () => {
  document.getElementById('jobExit').click();
})

// A new op with no hours typed in gets its op code's standard Hours (Settings > Op
// codes), else the storage-contract catalog's. Without this, storage-contract ops
// landed in the Scheduler backlog at 0h, and Auto-fill skips every 0h job.
async function defaultOpHours(opCode) {
  if (!opCode) return '';
  try {
    const rec = await idbGet('opCodeData', String(opCode));
    if (rec && parseFloat(rec.Hours) > 0) return String(rec.Hours);
  } catch (_) {}
  const entry = STORAGE_SERVICE_CATALOG.find(e => e.opCode === opCode);
  return entry && entry.hours ? String(entry.hours) : '';
}

// An op added with no Op_Type (storage contracts pass none) takes its op code's.
async function defaultOpType(opCode) {
  if (!opCode) return '';
  try {
    const rec = await idbGet('opCodeData', String(opCode));
    if (rec && rec.Op_Type) return String(rec.Op_Type);
  } catch (_) {}
  const entry = STORAGE_SERVICE_CATALOG.find(e => e.opCode === opCode);
  return entry?.opType || '';
}

export async function addJob(standard, currentWo) {

  let newData = []
  var data = []

  document.getElementById('active').scrollIntoView();

  const selectedWorkOrderType = document.getElementById('work-order-type-select').value

  var storageCheck = document.getElementById('work-order-form-storage-confirm').checked
  var storagePrice = document.getElementById('storagePrice').innerHTML
  var price = parseFloat(storagePrice.replace(/[$,]/g, ''));
  var length = document.getElementById('work-order-form-boat-length').value
  var beam = document.getElementById('work-order-form-boat-beam').value
  var mooringWeight = document.getElementById('work-order-form-boat-mooring-weight').value
  var mooring = document.getElementById('work-order-form-boat-mooring-number-slip').value
  var combo = document.getElementById('work-order-form-boat-combo').value
  var customerId = document.getElementById('work-order-form-customer-id').value;
  var boat = document.getElementById('work-order-form-boat-name').value;
  var boatId = document.getElementById('work-order-form-boat-id').value;
  var engineCount = document.getElementById('work-order-form-boat-engine-count').value;
  var batteryCount = document.getElementById('work-order-form-boat-battery-count').value;
  var overallCategory = document.getElementById('overallCategory').value
  var generalWorkOrder = document.getElementById('generalWorkOrder').value;
  var winterWorkOrder = document.getElementById('winterWorkOrder').value;
  var springWorkOrder = document.getElementById('springWorkOrder').value;
  var shrinkwrapWorkOrder = document.getElementById('shrinkwrapWorkOrder').value;
  var email = document.getElementById('work-order-form-customer-email').value;
  var name = document.getElementById('work-order-form-customer-name').value;
  if (name.includes(',')) {
    var firstName = name.split(', ')[1]
    var lastName = name.split(', ')[0]
  } else {
    var firstName = ''
    var lastName = name
  }

  if (overallCategory !== '') {
    var category = overallCategory
  } else { var category = document.getElementById('category').value }

  if (customerId === "") {
    toastMessage('No Customer Selected', "red");
    document.getElementById('customerDatabaseButton').click();
    return
  }

  if (currentWo) {

    var workOrder = currentWo

  } else {

    // The number is taken here, with the first operation, unless the office already generated it.
    if (category === "Winterizing" && selectedWorkOrderType === 'storage-work-order') {
      var workOrder = await ensureWorkOrderNumber('winter')
      if (!workOrder) return
    }

    if (category === "Spring Commissioning" && selectedWorkOrderType === 'storage-work-order') {
      var workOrder = await ensureWorkOrderNumber('spring')
      if (!workOrder) return
    }

    if (selectedWorkOrderType === 'general-work-order') {
      var workOrder = await ensureWorkOrderNumber('general')
      if (!workOrder) return
    }

     if (category === "Winterizing" && selectedWorkOrderType === 'storage-work-order' && document.getElementById('description').value.toLowerCase().includes('shrinkwrap')) {

      wakeShrinkwrapCard();

      var workOrder = await ensureWorkOrderNumber('shrinkwrap')
      if (!workOrder) return
    }


    if (generalWorkOrder === "" && winterWorkOrder === "" && springWorkOrder === "" && shrinkwrapWorkOrder === "" && selectedWorkOrderType === 'storage-work-order') {
      toastMessage('No Work Order Number', "red")
      return
    }

    // Catch-all: if no branch above assigned workOrder it will be undefined
    if (!workOrder) {
      toastMessage('No Work Order Number', "red")
      return
    }

  }

  if (standard === "bottom painting") {
    var opCode = "SP19"
    var opWeight = "1"
    var category = "Spring Commissioning"
    var opType = "Bottom Paint"
    var dmShortDesc = "BOTTOM PAINT, STORAGE"
    var description = 'BOTTOM PAINT, STORAGE : Paint boat with one (1)  coat of anti-fouling paint. Included with winter storage. (No Additional Charge)'

  } else if (standard === "hauling") {
    var opCode = "WS01"
    var opWeight = "0"
    var category = "Winterizing"
    var opType = "Hauling"
    var dmShortDesc = "HAUL BOAT, STORAGE"
    var description = "HAUL BOAT, STORAGE : Haul boat and block for storage. Check for scratches, loose or missing paint, and any other damage.  Included with winter storage (No Additional Charge)"
  } else if (standard === "launching") {
    var opCode = "SP37"
    var opWeight = "0"
    var category = "Spring Commissioning"
    var opType = "Hauling"
    var dmShortDesc = "LAUNCH BOAT, STORAGE"
    var description = "LAUNCH BOAT, STORAGE : Launch boat. .  Check for leaks in all readily accessible areas below the waterline.  Included with winter storage. (No Additional Charge)"
  } else if (standard === "pressure washing") {
    var opCode = "WS02"
    var opWeight = "0"
    var category = "Winterizing"
    var opType = "Cleaning"
    var dmShortDesc = "PRESSURE WASH, STORAGE"
    var description = "PRESSURE WASH, STORAGE : Pressure wash bottom.  Use cleaner to remove yellowing at waterline. Included with winter storage (No Additional Charge)"
  } else {
    var opCode = document.getElementById('opCode').value
    var opWeight = document.getElementById('opWeight').value !== '' ? document.getElementById('opWeight').value : '0'
    var opType = document.getElementById('opType').value
    var description = document.getElementById('description').value
    var dmShortDesc = document.getElementById('dmShortDesc').value

  }

  if (document.getElementById('date').value == '') {
    var date = new Date()
    var date = new Date().toLocaleDateString('en-us', { year: "numeric", month: "2-digit", day: "2-digit", timeZone: 'UTC' })
    var year = date.split('/')[2]
    var month = date.split('/')[0]
    var day = date.split('/')[1]
    var date = year + "-" + month + "-" + day
    date.toString()
  } else { var date = document.getElementById('date').value; }

  var status = document.getElementById('status').value;
  var hours = document.getElementById('estimatedHours').value;
  if (!(parseFloat(hours) > 0)) hours = await defaultOpHours(opCode);
  if (!opType) opType = await defaultOpType(opCode);
  var charge = document.getElementById('estimatedCharge').value
  var flatPerFoot = document.getElementById('flatPerFoot').value
  var flatLabor = document.getElementById('flatLabor').value
  var flatMethod = document.getElementById('flatMethod').value

  if (document.getElementById('begin').value === '') {
    var begin = date
    var schedule = ''
  } else {
    var begin = new Date(document.getElementById('begin').value).toLocaleDateString('en-us', { year: "numeric", month: "2-digit", day: "2-digit", timeZone: 'UTC' })
    var beginYear = begin.split('/')[2]
    var beginMonth = begin.split('/')[0]
    var beginDay = begin.split('/')[1]
    var begin = beginYear + "-" + beginMonth + "-" + beginDay
    begin.toString();
    var schedule = begin;
  }
  var launch = document.getElementById('work-order-form-dates-launch-date').value
  var haul = document.getElementById('work-order-form-dates-haul-date').value

  if(category === 'Winterizing' && description.toLowerCase().includes('shrinkwrap')){
    var shortDesc = "Shrinkwrap"
  } else if (category === "Spring Commissioning") {
    var shortDesc = "Spring Commissioning"
  }else if(category === 'Winterizing'){
    var shortDesc = "Winterizing"
  } else { var shortDesc = document.getElementById('shortDesc').value }

  if (status.toString() === '') {
    var status = "On Work Schedule"
  } else { var status = status }

  if (description.toLowerCase().includes('winterize engine') || description.toLowerCase().includes('commission engine')) {
    var kit = "Needs Kit"
  } else { var kit = '' }

  if (description.toLowerCase().includes('engine') && Number(flatLabor) > 0 && Number(engineCount) >= 1) {
    var flatLabor = Number(flatLabor) * engineCount
    var description = description + ' ENGINE COUNT (' + engineCount + '), TOTAL FLAT RATE = $' + Number(flatLabor).toFixed(2)
  }

  if (description.toLowerCase().includes('engine') && Number(flatLabor) > 0 && (Number(engineCount) == 0 || !engineCount)) {
    var description = description + " ENGINE COUNT IS UNKNOWN.  NUMBER OF ENGINES REQUIRED FOR FLAT RATE CALCULATION."
  }

  if ((description.toLowerCase().includes('battery') || description.toLowerCase().includes('batteries')) && Number(flatLabor) > 0 && Number(batteryCount) >= 1) {
    var flatLabor = Number(flatLabor) * batteryCount
    var description = description + ' [ BATTERY COUNT (' + batteryCount + ') ]'
  }

  if ((description.toLowerCase().includes('battery') || description.toLowerCase().includes('batteries') && (Number(batteryCount) == 0 || !batteryCount))) {
    var description = description + " [ BATTERY COUNT IS UNKNOWN.  NUMBER OF BATTERIES REQUIRED FOR FLAT RATE CALCULATION. ]"
  }

  try {
    let customerDM = (await shell.getCustomerRecord(customerId).catch(() => null)) || {};
    var jobId = await generateId()
    let start
    if (category === "Spring Commissioning" && launch !== '') {
      start = launch
    } else if (category === 'Winterizing' && haul !== '') {
      start = haul
    } else { start = date }

    // "Schedule it now" pick (General Labor only) — see pendingOpSchedule.
    const assign = category === 'General Labor' && parseFloat(hours) > 0 ? pendingOpSchedule : null;
    if (assign) schedule = assign.start;

    var serviceLog = {
      "Billing User": "",
      "Boat_ID": boatId,
      "Boat_Name": boat,
      "Category": category,
      "Charge": charge,
      "Customer_ID": customerId,
      "Customer_Name": name,
      "Date Billed": "",
      "Date_Hauled": "",
      "Date_Launched": "",
      "Description": description,
      "Email": email,
      "Estimated_Hours": hours,
      "Event_Added": "",
      "Event_Time": "",
      "Haul_Date": haul,
      "Hold_Days": "",
      "Job_ID": jobId,
      "Labor Billed": "",
      "Launch_Date": launch,
      "Location_Number": "",
      "OpCode": opCode,
      "Op_Type": opType,
      "Operation_Dependency": opWeight,
      "Parts Billed": "",
      "Parts Ordered": "",
      "Parts_Kit_Status": "",
      "Priority": "",
      "Ready": "",
      "Request_Date": date,
      "Schedule_Date": schedule,
      "Short_Description": shortDesc,
      "Short_Description_DM": dmShortDesc,
      "Start_Date": start,
      "Status": status,
      "Status_Of_Boat": "",
      "Techs": assign ? assign.tech : "",
      "Total_Hours": 0,
      "Transfer_to_Billing": "",
      "Water": "",
      "Work_Order": workOrder,
      "Work_Order_ID": "",
      "Yard": ""
    };

    let type
    if (category.toLowerCase() === 'internal') {
      type = 'I'
    } else { type = 'R' }

    if (!serviceLog.Work_Order || serviceLog.Work_Order.toString().trim() === "") {
      toastMessage("No Selected Work Order for This Operation", 'red')
      return
    }

    document.getElementById('job').style.display = 'none'

    const serviceLogForDM = {
      ...serviceLog,
      "type": type,
      "DMCustomer": customerDM.DM_Customer,
      "clerkId": shell.clerkIdInfo[auth.currentUser.displayName],
      "Title": document.getElementById('shortDesc').value
    };

    const serviceLogForIndexed = {
      ...serviceLog,
      "id": serviceLog.Job_ID
    };

    await shell.addJobFirebase(serviceLog);
    await updateOpenDataDB(serviceLogForIndexed);
    if (!suppressWorkOrderAddedToast) toastMessage('Work Order Added', 'green');

    const newOp = await createNewOpRow(serviceLog);

    if (shell.jobsByWorkOrder) {
      if (!shell.jobsByWorkOrder.has(serviceLog.Work_Order)) shell.jobsByWorkOrder.set(serviceLog.Work_Order, []);
      const arr = shell.jobsByWorkOrder.get(serviceLog.Work_Order);
      // Idempotent by Job_ID — see the matching comment on the onChildAdded(jobsRef, ...) handler
      // above; that listener's own push can race and beat this one for our own just-written job.
      if (!arr.some(j => j.Job_ID === serviceLog.Job_ID || j.id === serviceLog.Job_ID)) arr.push(serviceLog);
    }

    const opsTable = document.getElementById('operations-table');
    if (opsTable && newOp && !opsTable.querySelector(`.op-table-data-row[data-id="${CSS.escape(String(serviceLog.Job_ID))}"]`)) {
      opsTable.appendChild(newOp);
    }
    refreshWorkOrderRow(serviceLog.Work_Order);

    // The operation is already saved; DockMaster sync carries on in the background. This used to
    // reject with a function (so its toast never ran and it logged as an addJob error); a slow
    // DockMaster isn't an error, so just note it.
    const timeout = new Promise((resolve) => setTimeout(() => resolve('timeout'), 2000));
    const dmResult = await Promise.race([shell.updateWorkOrderDM(serviceLogForDM), timeout]);
    if (dmResult === 'timeout') {
      console.warn('addJob: DockMaster update still running after 2s; the operation is saved and sync continues.');
    }

  } catch (error) {
    console.error("An error occurred in the addJob function: ", error);
    // Handle any errors, such as showing a message to the user
  }

  let jobBox = document.createElement('div')
  jobBox.setAttribute('data-opCode', opCode)
  jobBox.setAttribute('data-workorder', workOrder)
  jobBox.setAttribute('data-job', jobId)
  jobBox.setAttribute('data-job-record', JSON.stringify(serviceLog))
  jobBox.className = 'jobBox wo-job-row'

  // G0 — "included" (one of the 4 storage-contract auto-adds) vs "added"
  // (picked manually) is derived from `standard`, same as the opCode/desc
  // branch above — there's no separate stored flag, so this must stay in
  // sync with that branch's list of standard values.
  const isIncludedStorageOp = ["bottom painting", "hauling", "launching", "pressure washing"].includes(standard);
  if (isIncludedStorageOp) jobBox.classList.add('wo-job-row-included');

  let jobDesc = document.createElement('p')
  jobDesc.className = 'wo-job-row-desc';
  jobDesc.textContent = opCode + ": " + description.split(':')[0];
  jobDesc.setAttribute('data-opCode', opCode)
  jobDesc.setAttribute('data-workorder', workOrder)

  // Status pill + hours — read-only display of what was just saved (the
  // status/hours already chosen on the op-detail panel), same tone scale
  // used everywhere else (statusToneSlug, see the op-table-data-row CSS).
  const jobMeta = document.createElement('div');
  jobMeta.className = 'wo-job-row-meta';
  // G0's included/added pill only applies to storage contracts — general
  // and estimate work orders don't have an "included with storage" concept.
  if (selectedWorkOrderType === 'storage-work-order') {
    const includedPill = document.createElement('span');
    includedPill.className = isIncludedStorageOp ? 'wo-job-row-included-pill' : 'wo-job-row-added-pill';
    includedPill.textContent = isIncludedStorageOp ? 'included' : 'added';
    jobMeta.appendChild(includedPill);
  }
  if (status) {
    const pill = document.createElement('span');
    pill.className = 'wo-job-row-status op-tone-' + statusToneSlug(status);
    pill.textContent = status;
    jobMeta.appendChild(pill);
  }
  if (hours) {
    const hoursEl = document.createElement('span');
    hoursEl.className = 'wo-job-row-hours';
    hoursEl.textContent = `${hours}h`;
    jobMeta.appendChild(hoursEl);
  }

  const jobActions = document.createElement('div');
  jobActions.className = 'wo-job-row-actions';

  const editBtn = document.createElement('button');
  editBtn.type = 'button';
  editBtn.className = 'wo-job-row-icon-btn';
  editBtn.innerHTML = '&#9998;';
  editBtn.title = 'Edit operation';
  editBtn.addEventListener('click', (e) => { e.stopPropagation(); openEditOperation(jobBox); });

  const removeBtn = document.createElement('button');
  removeBtn.type = 'button';
  removeBtn.className = 'wo-job-row-icon-btn wo-job-row-icon-danger';
  removeBtn.innerHTML = '&times;';
  removeBtn.title = 'Remove operation';
  removeBtn.addEventListener('click', (e) => { e.stopPropagation(); confirmRemoveOperation(jobBox); });

  jobActions.appendChild(editBtn);
  jobActions.appendChild(removeBtn);

  jobBox.appendChild(jobDesc)
  jobBox.appendChild(jobMeta)
  jobBox.appendChild(jobActions)

  if(document.querySelector('.active-op')){
    document.querySelector('.active-op').classList.add('hidden');
    document.querySelector('.active-op').classList.remove('active-op');
    shell.currentOpCodes.push(opCode);
  }

  document.getElementById('opCode').value = ''
  document.getElementById("opType").value = ""
  document.getElementById("description").value = ""
  document.getElementById("date").value = ""
  document.getElementById("status").value = ""
  document.getElementById("begin").value = ""
  document.getElementById('estimatedHours').value = "";


  const firstOp = !document.querySelector('#activeJobs .wo-job-row');
  jobBox.classList.add('wo-job-row-new');
  setTimeout(() => jobBox.classList.remove('wo-job-row-new'), 2000);

  if (!document.getElementById(workOrder)) {

    let field = document.createElement('fieldset')
    field.id = workOrder.toString()
    let legend = document.createElement('legend')
    if (category === "Winterizing") {
      var woType = "(Winterizing)"
    }
    if (category === "Spring Commissioning") {
      var woType = "(Spring Commissioning)"
    }
    if(category === "Winterizing" && description.toLowerCase().includes('shrinkwrap')){
      var woType = "(Shrinkwrap)"
    }

    if (woType) {
      legend.innerHTML = workOrder.toString() + " " + woType
    } else { legend.innerHTML = workOrder.toString() }

    field.appendChild(legend)
    field.appendChild(jobBox)

    document.getElementById('activeJobs').appendChild(field)

  } else { document.getElementById(workOrder).appendChild(jobBox) }

  if (document.getElementById('addModal')?.classList.contains('wo-picking')) jobBox.scrollIntoView({ block: 'nearest' });

  // A general work order's Category follows its first operation, until the office picks one.
  const categorySelect = document.getElementById('categorySelect');
  if (firstOp && selectedWorkOrderType === 'general-work-order' && categorySelect && !categorySelect.dataset.userSet
    && category !== categorySelect.value && [...categorySelect.options].some(o => o.value === category)) {
    categorySelect.value = category;
    categorySelect.dispatchEvent(new Event('change'));
  }

  updateWoOpsSummary();
  renderWoPickSummary();
}

// "N added" + hours subtotal in the new Operations card header — purely a
// read of what's already in #activeJobs, no new state.
export function updateWoOpsSummary() {
  const countEl = document.getElementById('wo-ops-count');
  const hoursEl = document.getElementById('wo-ops-hours');
  if (!countEl || !hoursEl) return;
  const rows = document.querySelectorAll('#activeJobs .wo-job-row');
  let totalHours = 0;
  rows.forEach(row => {
    const h = parseFloat(row.querySelector('.wo-job-row-hours')?.textContent);
    if (!isNaN(h)) totalHours += h;
  });
  countEl.textContent = rows.length ? `${rows.length} added` : '';
  hoursEl.textContent = totalHours > 0 ? `${totalHours.toFixed(1)}h` : '';
  renderWoValidationChecklist();
}

export function deleteJob(e) {
  if (e.innerHTML === "Delete Job") {
    var job = document.getElementById(e.parentElement.id)
    document.getElementById(e.parentElement.parentElement.style.display = 'none')
  } else { var job = document.getElementById(e.parentElement.getAttribute('data-job')) }
  job.classList.remove('active')
  job.classList.add('inactive')
  const number = job.id.at(-1)

  document.getElementById('jobExit' + number).classList.remove('hidden')
  document.getElementById('jobMinimize' + number).classList.add('hidden')
  document.getElementById('addJobButton' + number).classList.remove('hidden')
  document.getElementById('deleteJobButton' + number).classList.add('hidden')
  const opCode = document.getElementById("opCode" + number).value
  document.getElementById(opCode).classList.remove('hidden')
  document.getElementById('opCode' + number).value = ''
  document.getElementById('category' + number).value = ''
  document.getElementById('opType' + number).value = ''
  document.getElementById('date' + number).value = ''
  document.getElementById('tech' + number).value = ''
  document.getElementById('begin' + number).value = ''
  document.getElementById('status' + number).value = ''
  document.getElementById('button' + number).remove()

}

// ============ Edit / remove an already-added operation (in-place, Part E) ============
//
// Reuses the exact same #job/#opCodeForm fields and rebuildOpDescription()
// plumbing as adding a new op — the row's full serviceLog was stashed on
// jobBox as data-job-record when it was first added, so nothing is re-fetched.

export let editingOperationJobBox = null;

function exitEditOperationMode() {
  editingOperationJobBox = null;
  const btn = document.getElementById('addJobButton');
  if (btn) btn.textContent = 'Add operation';
}

function openEditOperation(jobBox) {
  let record;
  try {
    record = JSON.parse(jobBox.getAttribute('data-job-record') || '{}');
  } catch {
    toastMessage('Could not load this operation for editing', 'red');
    return;
  }

  editingOperationJobBox = jobBox;

  document.getElementById('category').value = record.Category || '';
  document.getElementById('opType').value = record.Op_Type || '';
  document.getElementById('opCode').value = record.OpCode || '';
  document.getElementById('opWeight').value = record.Operation_Dependency || '';
  document.getElementById('dmShortDesc').value = record.Short_Description_DM || '';
  document.getElementById('description').value = record.Description || '';
  document.getElementById('shortDesc').value = record.Short_Description || '';
  document.getElementById('status').value = record.Status || '';
  document.getElementById('estimatedHours').value = record.Estimated_Hours || '';
  document.getElementById('estimatedCharge').value = record.Charge || '';
  document.getElementById('begin').value = record.Start_Date || '';
  document.getElementById('date').value = record.Request_Date || '';

  setCurrentOpCodeData({
    additional: '', category: record.Category, opCode: record.OpCode, opType: record.Op_Type,
    dependency: record.Operation_Dependency, flatRateLength: '', hours: record.Estimated_Hours,
    description: record.Description, flatRatePerFoot: '', flatRateLabor: ''
  });

  const lowerDesc = (record.Description || '').toLowerCase();
  const mooringField = document.getElementById('mooringNumberField');
  const mooringWeightField = document.getElementById('mooringWeightField');
  const coatsField = document.getElementById('coatsField');
  const discountField = document.getElementById('discountField');

  mooringField.style.display = lowerDesc.includes('mooring') ? 'block' : 'none';
  mooringWeightField.style.display = lowerDesc.includes('mooring') ? 'block' : 'none';
  coatsField.style.display = (lowerDesc.includes('bottom paint') && !lowerDesc.includes('storage')) ? 'block' : 'none';
  discountField.style.display = record.OpCode === 'DISCNT' ? 'block' : 'none';

  const coatsMatch = (record.Description || '').match(/\((\d+)\)\s*coats?/i);
  if (coatsMatch) document.getElementById('opCoatsCount').value = coatsMatch[1];

  const addBtn = document.getElementById('addJobButton');
  addBtn.textContent = 'Save changes';
  jobAssist.reset();

  document.getElementById('job').style.display = 'flex';
}

export async function saveEditedOperation(jobBox) {
  const jobId = jobBox.getAttribute('data-job');
  const workOrder = jobBox.getAttribute('data-workorder');
  if (!jobId) return;

  const updates = {
    Category: document.getElementById('category').value,
    Op_Type: document.getElementById('opType').value,
    OpCode: document.getElementById('opCode').value,
    Short_Description_DM: document.getElementById('dmShortDesc').value,
    Description: document.getElementById('description').value,
    Short_Description: document.getElementById('shortDesc').value,
    Status: document.getElementById('status').value,
    Estimated_Hours: document.getElementById('estimatedHours').value,
    Charge: document.getElementById('estimatedCharge').value
  };

  try {
    const idToken = await fetchIdToken();
    const jobUrl = `${shell.currentURL}/data/${jobId}.json?auth=${idToken}`;
    await fetch(jobUrl, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates) });

    const existing = await idbGet('jobData', jobId);
    if (existing) await idbPut('jobData', { ...existing, ...updates });

    const record = { ...JSON.parse(jobBox.getAttribute('data-job-record') || '{}'), ...updates };
    jobBox.setAttribute('data-job-record', JSON.stringify(record));
    jobBox.setAttribute('data-opCode', updates.OpCode);

    const descEl = jobBox.querySelector('.wo-job-row-desc');
    if (descEl) descEl.textContent = updates.OpCode + ': ' + (updates.Description || '').split(':')[0];

    const metaEl = jobBox.querySelector('.wo-job-row-meta');
    if (metaEl) {
      metaEl.innerHTML = '';
      if (updates.Status) {
        const pill = document.createElement('span');
        pill.className = 'wo-job-row-status op-tone-' + statusToneSlug(updates.Status);
        pill.textContent = updates.Status;
        metaEl.appendChild(pill);
      }
      if (updates.Estimated_Hours) {
        const hoursEl = document.createElement('span');
        hoursEl.className = 'wo-job-row-hours';
        hoursEl.textContent = `${updates.Estimated_Hours}h`;
        metaEl.appendChild(hoursEl);
      }
    }

    updateWoOpsSummary();
    toastMessage('Operation updated', 'green');
    document.getElementById('job').style.display = 'none';
    exitEditOperationMode();
  } catch (error) {
    console.error('Error saving edited operation:', error);
    toastMessage('Error saving changes', 'red');
  }
}

function confirmRemoveOperation(jobBox) {
  if (jobBox.classList.contains('wo-job-row-confirming')) return;
  jobBox.classList.add('wo-job-row-confirming');
  const original = jobBox.innerHTML;

  jobBox.innerHTML = `
    <div class="wo-job-row-confirm-text">Remove this operation?</div>
    <div class="wo-job-row-confirm-actions">
      <button type="button" class="wo-job-row-confirm-remove">Remove</button>
      <button type="button" class="wo-job-row-confirm-cancel">Cancel</button>
    </div>
  `;

  jobBox.querySelector('.wo-job-row-confirm-cancel').addEventListener('click', (e) => {
    e.stopPropagation();
    jobBox.innerHTML = original;
    jobBox.classList.remove('wo-job-row-confirming');
  });

  jobBox.querySelector('.wo-job-row-confirm-remove').addEventListener('click', async (e) => {
    e.stopPropagation();
    await removeAddedOperation(jobBox);
  });
}

async function removeAddedOperation(jobBox) {
  const jobId = jobBox.getAttribute('data-job');
  const workOrder = jobBox.getAttribute('data-workorder');
  const opCode = jobBox.getAttribute('data-opCode');

  try {
    const user = await getCurrentUserData();
    const idToken = await fetchIdToken();
    const updates = {
      Status: 'Declined',
      Declined_Note: 'Removed while building the work order',
      Declined_User: (user && (user.displayName || user.email)) || 'Unknown',
      Declined_Date: await shell.formatDate(),
      DM_Delete_Pending: true
    };
    const jobUrl = `${shell.currentURL}/data/${jobId}.json?auth=${idToken}`;
    await fetch(jobUrl, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates) });

    const existing = await idbGet('jobData', jobId);
    if (existing) await idbPut('jobData', { ...existing, ...updates });

    let dmOk = true;
    try { await shell.deleteOperationDM(workOrder, opCode); } catch { dmOk = false; }

    const postDM = dmOk
      ? { DM_Delete_Pending: false, DM_Deleted: true, DM_Deleted_At: await shell.formatDate() }
      : { DM_Delete_Pending: false, DM_Deleted: false };
    await fetch(jobUrl, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(postDM) });

    const fieldset = jobBox.closest('fieldset');
    jobBox.remove();
    if (fieldset && !fieldset.querySelector('.wo-job-row')) fieldset.remove();
    updateWoOpsSummary();
    // Keep the rail's mini "auto"/"queued" preview in sync even when an
    // included op is removed via this button instead of the toggle.
    if (typeof renderIncludedOpsPreview === 'function') {
      renderIncludedOpsPreview('winter');
      renderIncludedOpsPreview('spring');
    }
    toastMessage(dmOk ? 'Operation removed' : 'Operation removed (Dockmaster sync failed; recorded for follow-up)', dmOk ? 'green' : 'yellow');
  } catch (error) {
    console.error('Error removing operation:', error);
    toastMessage('Error removing operation', 'red');
  }
}

function renderWoNumberState(type, state, opts = {}) {
  const card = document.getElementById(`${type}-wo-card`);
  const pill = document.getElementById(`${type}-wo-status-pill`);
  if (card) card.querySelectorAll('.wo-number-message').forEach(el => el.remove());
  if (pill) {
    pill.className = 'wo-number-status-pill wo-status-' + state;
    pill.textContent = state === 'assigned' ? '✓ Assigned'
      : state === 'blocked' ? 'Blocked'
      : state === 'failed' ? 'Failed'
      : 'Automatic';
  }
  if (card && (state === 'failed' || state === 'blocked')) {
    const box = document.createElement('div');
    box.className = `wo-number-message wo-number-message-${state}`;
    box.innerHTML = `
      <div style="flex:1;">
        <span class="wo-number-message-title">${opts.title || ''}</span>
        ${opts.detail || ''}
      </div>
      <button type="button" class="wo-number-message-action">${state === 'failed' ? 'Retry' : 'Link in Customers'}</button>
    `;
    box.querySelector('.wo-number-message-action').addEventListener('click', () => {
      if (state === 'failed') getWorkOrderNumber(type);
      else window.open('/customers.html', '_blank');
    });
    card.appendChild(box);
  }
  renderWoValidationChecklist();
}

// Dockmaster is bypassed for WO numbering (see DOCKMASTER_MOCK_MODE in
// functions/index.js) — numbers come from the same internal sequential
// counter fuel.js's createComplianceWorkOrder() uses, not a Dockmaster call.
// The form's number for this season/type, taking the next one first if there is none yet.
// Shared per type while it's being taken, so two quick adds can't take two numbers.
const woNumberPending = {};
async function ensureWorkOrderNumber(type) {
  const input = document.getElementById(`${type}WorkOrder`);
  if (input?.value) return input.value;
  woNumberPending[type] ||= getWorkOrderNumber(type).finally(() => { delete woNumberPending[type]; });
  return (await woNumberPending[type]) || input?.value || '';
}

export async function getWorkOrderNumber(type) {

  if (document.getElementById('work-order-form-customer-id').value === '') {
    toastMessage("No Customer Selected", 'red');
    return;
  }

  const workOrderButton = document.getElementById(`${type}-work-order-button`);
  const loadingIcon = document.getElementById(`${type}-loading-icon`);

  workOrderButton.style.display = "none";
  loadingIcon.style.display = "flex";
  renderWoNumberState(type, 'pending');

  try {
    const seqRef = ref(database, '/meta/nextWorkOrderSeq');
    const result = await runTransaction(seqRef, (curr) => (curr || 2200) + 1);
    const workOrderNumber = `WO-${result.snapshot.val()}`;

    document.getElementById(`${type}WorkOrder`).value = workOrderNumber;
    workOrderButton.style.display = "inline-block";
    loadingIcon.style.display = "none";
    renderWoNumberState(type, 'assigned');

    const field = document.createElement('fieldset');
    field.id = workOrderNumber.toString();
    const legend = document.createElement('legend');
    let woType = "";
    if (type === "winter") {
      woType = "(Winterizing)";
    } else if (type === "spring") {
      woType = "(Spring Commissioning)";
    }else if(type === "shrinkwrap"){
      woType = "(Shrinkwrap)";
    }
    legend.innerHTML = woType ? `${workOrderNumber} ${woType}` : workOrderNumber.toString();
    field.appendChild(legend);

    document.getElementById('activeJobs').appendChild(field);
    const storageCheck = document.getElementById('work-order-form-storage-confirm').checked;

    // G2 — same add path the toggle itself uses (see includedStorageOps()
    // above), so a WO generated with the toggle already on behaves
    // identically to toggling on after the fact.
    if (storageCheck) {
      await addIncludedStorageOps(type, workOrderNumber);
    } else if (typeof renderIncludedOpsPreview === 'function') {
      renderIncludedOpsPreview(type);
    }

    return workOrderNumber;
  } catch (error) {
    console.error('Error creating work order:', error);
    toastMessage(`Error creating work order: ${error.message}`, 'red');
    workOrderButton.style.display = "inline-block";
    loadingIcon.style.display = "none";
    renderWoNumberState(type, 'failed', {
      title: `Couldn't assign a work order number`,
      detail: `Nothing was created. The draft and its operations are safe.`
    });
  }
}

export async function getWorkOrderNumberMove(clerkId, dmCustomerId, dmBoatId, type, title) {
  if (!usesDockmaster()) return nextInternalWorkOrderNumber();
  const functions = getFunctions(app);
  const createWorkOrderFunction = httpsCallable(functions, 'createDockmasterWorkOrder');

  const data = {
    clerkId: clerkId,
    dmCustomerId: dmCustomerId,
    dmBoatId: dmBoatId,
    type: type
  };

  if(title){
    data.title = title;
  }

  try {
    const result = await createWorkOrderFunction(data);
    return result.data.workOrderNumber;
  } catch (error) {
    console.error('Error creating work order:', error);
    throw error;
  }
}

export function populateTable(uniqueData) {
  var tableBody = document.getElementById('customer-table-body');
  // Clear existing rows
  tableBody.innerHTML = '';

  // Loop through uniqueData array and add rows
  for (var i = 0; i < uniqueData.length; i++) {
    var row = document.createElement('tr');

    var idCell = document.createElement('td');
    idCell.innerText = uniqueData[i].Customer_ID;
    idCell.style.display = 'none';

    var fullNameCell = document.createElement('td');
    fullNameCell.innerText = uniqueData[i].Last_Name + ', ' + uniqueData[i].First_Name

    var lastNameCell = document.createElement('td');
    lastNameCell.innerText = uniqueData[i].Last_Name;
    lastNameCell.style.display = 'none';

    var firstNameCell = document.createElement('td');
    firstNameCell.innerText = uniqueData[i].First_Name;
    firstNameCell.style.display = 'none';

    var streetCell = document.createElement('td');
    streetCell.innerText = uniqueData[i].Street;
    streetCell.style.display = 'none';

    var cityCell = document.createElement('td');
    cityCell.innerText = uniqueData[i].City;
    cityCell.style.display = 'none';

    var stateCell = document.createElement('td');
    stateCell.innerText = uniqueData[i].State;
    stateCell.style.display = 'none';

    var zipCell = document.createElement('td');
    zipCell.innerText = uniqueData[i].Zip;
    zipCell.style.display = 'none';

    var phoneCell = document.createElement('td');
    phoneCell.innerText = uniqueData[i].Phone;
    phoneCell.style.display = 'none';

    var emailCell = document.createElement('td');
    emailCell.innerText = uniqueData[i].Email;
    emailCell.style.display = 'none';

    var customerDM = document.createElement('td');
    customerDM.innerText = uniqueData[i].DM_Customer;
    customerDM.style.display = 'none';

    // Append all cells to the row
    row.append(idCell, fullNameCell, lastNameCell, firstNameCell, streetCell, cityCell, stateCell, zipCell, phoneCell, emailCell, customerDM);

    // Create a scoped function to attach the click event
    (function () {
      var rowData = {
        Customer_ID: uniqueData[i].Customer_ID,
        Last_Name: uniqueData[i].Last_Name,
        First_Name: uniqueData[i].First_Name,
        Street: uniqueData[i].Street,
        City: uniqueData[i].City,
        State: uniqueData[i].State,
        Zip: uniqueData[i].Zip,
        Phone: uniqueData[i].Phone,
        Email: uniqueData[i].Email,
        DM_Customer: uniqueData[i].DM_Customer
      };

      row.addEventListener('click', function () {
        console.log(rowData);
        document.getElementById('customerFormId').value = rowData.Customer_ID
        document.getElementById('customerLastName').value = rowData.Last_Name;
        document.getElementById('customerFirstName').value = rowData.First_Name;
        document.getElementById('customerStreet').value = rowData.Street;
        document.getElementById('customerCity').value = rowData.City;
        document.getElementById('customerState').value = rowData.State;
        document.getElementById('customerZip').value = rowData.Zip;
        document.getElementById('customerPhone').value = rowData.Phone;
        document.getElementById('customerEmail').value = rowData.Email;
        document.getElementById('customerDM').value = rowData.DM_Customer;

        document.getElementById('addNewCustomer').style.display = "flex"
        document.getElementById('currentCustomerTable').style.display = "none"

      });
    })();

    // Append the row to the table body
    tableBody.appendChild(row);
  }
}

document.getElementById('work-order-type-select').addEventListener('change', function () {
  const selectedValue = this.value;
  const storage = document.getElementById('winter-storage-section')
  const winterSpring = document.getElementById('storage-work-order-section')
  const dates = document.getElementById('work-order-date-section')
  const general = document.getElementById('general-work-order-section')

  // G — the storage rail (toggle+price card, Generate-both, 3 season cards)
  // needs more width than General's simpler column; widen the modal and its
  // left column specifically for Storage Contract instead of cramming it
  // into the same 392px track (see .wo-create-modal--storage in style.css).
  document.getElementById('addModal')?.classList.toggle('wo-create-modal--storage', selectedValue === 'storage-work-order');

  if(selectedValue === 'storage-work-order'){

    storage.classList.remove('hidden-form-section');
    dates.classList.remove('hidden-form-section');
    winterSpring.classList.remove('hidden-form-section')
    winterSpring.style.display='flex'
    general.classList.add('hidden-form-section');
    general.style.display='none';

    // G3 — reopening a draft that already has a shrinkwrap WO (or an
    // already-added shrinkwrap op) should show the card awake immediately,
    // not just after the next shrinkwrap op is touched.
    const hasShrinkwrapOpAlready = Array.from(document.querySelectorAll('#activeJobs .wo-job-row-desc'))
      .some(el => el.textContent.toLowerCase().includes('shrinkwrap'));
    if (document.getElementById('shrinkwrapWorkOrder').value !== '' || hasShrinkwrapOpAlready) {
      wakeShrinkwrapCard();
    }

    const hiddenCategories = document.querySelectorAll('.hide-storage')
    hiddenCategories.forEach(category => {
      category.style.display = 'none';
    });

  }else if(selectedValue !== 'storage-work-order' && !storage.classList.contains('hidden-form-section')){

    storage.classList.add('hidden-form-section');
    dates.classList.add('hidden-form-section');
    winterSpring.classList.add('hidden-form-section')
    winterSpring.style.display='none';
    general.classList.remove('hidden-form-section');
    general.style.display='flex';

    const hiddenCategories = document.querySelectorAll('.hide-storage')
    hiddenCategories.forEach(category => {
      category.style.display = 'block';
    });

  }

  syncEstimateModeVisibility(selectedValue === 'estimate');

});

// Toggles every Estimate-only section on/off and swaps the shared
// topbar/Operations-card chrome between "Work Order" and "Estimate" wording.
// The General/Storage sections above are left alone — .hidden-form-section
// on the estimate-only elements is the only thing this touches.
function syncEstimateModeVisibility(isEstimate) {
  const general = document.getElementById('general-work-order-section');
  const estimateDetails = document.getElementById('estimate-details-section');
  const activeJobs = document.getElementById('activeJobs');
  const estimateOpsList = document.getElementById('estimate-ops-list');
  const partsSection = document.getElementById('estimate-parts-section');
  const pricingBar = document.getElementById('estimate-pricing-bar');
  const statusPill = document.getElementById('estimate-status-pill');
  const printBtn = document.getElementById('estimate-print-btn');
  const doneBtn = document.getElementById('wo-create-done-btn');
  const breadcrumbLabel = document.getElementById('wo-create-breadcrumb-label');

  [estimateDetails, estimateOpsList, partsSection, pricingBar, statusPill, printBtn].forEach(el => {
    if (!el) return;
    el.classList.toggle('hidden-form-section', !isEstimate);
    // .hidden-form-section (display:none) and .wo-estimate-details
    // (display:flex, declared later in style.css) have equal specificity,
    // so the class toggle alone doesn't reliably hide estimateDetails —
    // same reason `general` below needs an inline style, not just a class.
    el.style.display = isEstimate ? '' : 'none';
  });

  if (isEstimate) {
    general.classList.add('hidden-form-section');
    general.style.display = 'none';
    if (activeJobs) activeJobs.style.display = 'none';
    if (doneBtn) doneBtn.textContent = 'Save estimate';
    if (breadcrumbLabel) breadcrumbLabel.textContent = 'New Estimate';
  } else {
    // Storage<->General transitions are already owned by the block above
    // this function call; only re-show General here when coming *from*
    // Estimate mode into General (Storage's own branch already ran).
    const selectedValue = document.getElementById('work-order-type-select')?.value;
    if (selectedValue === 'general-work-order') {
      general.classList.remove('hidden-form-section');
      general.style.display = 'flex';
    }
    if (activeJobs) activeJobs.style.display = '';
    if (doneBtn) doneBtn.textContent = 'Create work order';
    if (breadcrumbLabel) breadcrumbLabel.textContent = 'New Work Order';
  }

  // #wo-ops-count/#wo-ops-hours are shared between the real-WO list and the
  // estimate list — refresh whichever one now owns them so switching type
  // mid-modal doesn't leave the other mode's stale count/hours showing.
  if (isEstimate) renderEstimateOperations(); else updateWoOpsSummary();
}

document.getElementById('work-order-form-dates-launch-date').addEventListener('change', () => {
  const customerId = document.getElementById('work-order-form-customer-id').value;
  const boatId = document.getElementById('work-order-form-boat-id').value;
  const index = 'Launch_Date';
  const date = document.getElementById('work-order-form-dates-launch-date').value;
  
  document.getElementById('work-order-form-dates-launch-date').setAttribute('date-updated', 'true');

  addHaulLaunchStorageDates(customerId, boatId, index, date)
  showWeekCounts('launch');
})

document.getElementById('work-order-form-dates-haul-date').addEventListener('change', () => {
  const customerId = document.getElementById('work-order-form-customer-id').value;
  const boatId = document.getElementById('work-order-form-boat-id').value;
  const index = 'Haul_Date';
  const date = document.getElementById('work-order-form-dates-haul-date').value;
  addHaulLaunchStorageDates(customerId, boatId, index, date)
  showWeekCounts('haul');
})

// G3 — the shrinkwrap WO card starts dormant (.wo-number-card-dormant, see
// markup) and wakes the first time a shrinkwrap op is touched, instead of
// only surfacing as a save-time toast (addJob()'s existing guard is
// untouched — this just makes the card it points to visible earlier).
export function wakeShrinkwrapCard() {
  document.getElementById('shrinkwrap-wo-card')?.classList.remove('wo-number-card-dormant');
}

// G2 — the 4 "included with winter storage" ops, keyed by season. Centralizes
// the addJob() standard-name + opCode pairs so the toggle's add/remove and
// getWorkOrderNumber()'s own auto-add (below) can't drift out of sync.
function includedStorageOps(type) {
  if (type === 'winter') return [{ standard: 'hauling', opCode: 'WS01' }, { standard: 'pressure washing', opCode: 'WS02' }];
  if (type === 'spring') return [{ standard: 'launching', opCode: 'SP37' }, { standard: 'bottom painting', opCode: 'SP19' }];
  return [];
}

// G1 rail — the small "auto"/"queued" preview rows under each season card
// (separate from the full Operations list on the right; this is just an
// at-a-glance summary). Re-derives its state from the DOM every time rather
// than tracking its own — same "no separate flag" approach as the
// included/added pill above.
const INCLUDED_OP_NAMES = {
  hauling: 'Haul & block',
  'pressure washing': 'Pressure wash',
  launching: 'Launch boat',
  'bottom painting': 'Bottom paint, 1 coat',
};

function renderIncludedOpsPreview(type) {
  const container = document.getElementById(`${type}-included-ops`);
  if (!container) return;
  const woInputId = type === 'winter' ? 'winterWorkOrder' : 'springWorkOrder';
  const workOrderNumber = document.getElementById(woInputId)?.value || '';
  container.innerHTML = '';
  includedStorageOps(type).forEach(({ standard, opCode }) => {
    const present = workOrderNumber && document.querySelector(`#${CSS.escape(workOrderNumber.toString())} .wo-job-row[data-opcode="${opCode}"]`);
    const row = document.createElement('div');
    row.className = 'wo-included-op-row' + (present ? '' : ' wo-included-op-row-pending');
    row.innerHTML = `<span class="wo-included-op-tag">${present ? 'auto' : 'queued'}</span><span class="wo-included-op-name">${INCLUDED_OP_NAMES[standard] || standard}</span><span class="wo-included-op-code">${opCode}</span>`;
    container.appendChild(row);
  });
}

// Adds whichever included ops for `type` aren't already under its WO fieldset
// yet. Safe to call repeatedly (toggle on/off/on, or generate-after-toggle)
// without creating duplicates — this is what makes the toggle and the
// Generate buttons order-independent instead of the old
// checked-at-generation-time-only coupling.
async function addIncludedStorageOps(type, workOrderNumber) {
  if (!workOrderNumber) return;
  for (const { standard, opCode } of includedStorageOps(type)) {
    const already = document.querySelector(`#${CSS.escape(workOrderNumber.toString())} .wo-job-row[data-opcode="${opCode}"]`);
    if (!already) await addJob(standard, workOrderNumber);
  }
  renderIncludedOpsPreview(type);
}

// Removes whichever included ops for `type` are currently present — runs
// when the storage toggle is switched off after a season WO already exists,
// through the same removeAddedOperation() path the × button uses (Declined
// status + Dockmaster delete), so turning the toggle off doesn't just hide
// state, it actually retracts the ops. Toggling back on re-adds them.
async function removeIncludedStorageOps(type) {
  const woInputId = type === 'winter' ? 'winterWorkOrder' : 'springWorkOrder';
  const workOrderNumber = document.getElementById(woInputId).value;
  if (!workOrderNumber) return;
  for (const { opCode } of includedStorageOps(type)) {
    const row = document.querySelector(`#${CSS.escape(workOrderNumber.toString())} .wo-job-row[data-opcode="${opCode}"]`);
    if (row) await removeAddedOperation(row);
  }
  renderIncludedOpsPreview(type);
}

document.getElementById('work-order-form-storage-confirm').addEventListener('click', async () => {

  const customerId = document.getElementById('work-order-form-customer-id').value;
  const boatId = document.getElementById('work-order-form-boat-id').value;
  const index = 'Storage_Date';
  const checked = document.getElementById('work-order-form-storage-confirm').checked;

  let date;
  if (checked === false) {
    date = '';
  } else {
    date = await getTodaysDate();
  }

  // Fixes the dead handler: date used to be computed and discarded here,
  // so Storage_Date was never actually saved.
  if (customerId && boatId) {
    await addHaulLaunchStorageDates(customerId, boatId, index, date);
  }

  const datePill = document.getElementById('wo-storage-date-pill');
  if (datePill) {
    if (checked && date) {
      datePill.textContent = new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      datePill.classList.remove('hidden');
    } else {
      datePill.classList.add('hidden');
    }
  }

  // G2 — order-independent auto-ops: add them now if a season WO already
  // exists (toggling on after Generate already ran), or retract them if
  // toggling off.
  const winterWo = document.getElementById('winterWorkOrder').value;
  const springWo = document.getElementById('springWorkOrder').value;
  if (checked) {
    if (winterWo) addIncludedStorageOps('winter', winterWo);
    if (springWo) addIncludedStorageOps('spring', springWo);
  } else {
    if (winterWo) removeIncludedStorageOps('winter');
    if (springWo) removeIncludedStorageOps('spring');
  }
})

export async function addHaulLaunchStorageDates(customerId, boatId, index, date) {
  // The new-WO form's date pickers fire before a customer and boat are picked; with
  // blank IDs the PATCH went to customers//Boats// — which Firebase collapses to a
  // stray /customers/Boats record that the techs app then listed as a customer.
  if (!customerId || !boatId) return;

  // Storage_Date has no "original" counterpart (unlike Haul/Launch), so it
  // intentionally leaves updateDate unset — see the guard below, which skips
  // adding that key instead of PATCHing a literal "undefined" property.
  let updateDate
  if (index == 'Launch_Date') {
    updateDate = 'Original_Launch_Date';
  } else if (index == 'Haul_Date') {
    updateDate = 'Original_Haul_Date';
  }

  try {
    const idToken = await fetchIdToken(); // Assume this fetches the ID token

    const firebaseUrl = `${shell.currentURL}/customers/${customerId}/Boats/${boatId}.json?auth=${idToken}`;

    const partialUpdate = updateDate
      ? { [index]: date, [updateDate]: date }
      : { [index]: date };

    const options = {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(partialUpdate),
    };

    const response = await fetch(firebaseUrl, options);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const result = await response.json();
    console.log("Operation successful", result);
    addHaulLaunchDB(customerId, boatId, index, date, updateDate)
  } catch (error) {
    console.error("Error modifying job status:", error);
  }
}

async function addHaulLaunchDB(customerId, boatId, index, value, updateDate) {
  try {
    const db = await getDB();
    const transaction = db.transaction("customerData", "readwrite");
    const objectStore = transaction.objectStore("customerData");

    const itemToUpdate = await new Promise((resolve, reject) => {
      const req = objectStore.get(customerId);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve(req.result || null);
    });

    if (!itemToUpdate) {
      console.error(`No item found with Customer ${customerId}`);
      return;
    }

    if (!itemToUpdate.Boats || !itemToUpdate.Boats[boatId]) {
      console.error(`No boat found with ID ${boatId} for Customer ${customerId}`);
      return;
    }

    itemToUpdate.Boats[boatId][index] = value;

    if (updateDate) {
      itemToUpdate.Boats[boatId][updateDate] = '';
    }

    await new Promise((resolve, reject) => {
      const req = objectStore.put(itemToUpdate);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve();
    });

    console.log(`Data updated for Customer ${customerId} and Boat ${boatId}`);
  } catch (error) {
    console.log("Error updating IndexedDB", error);
  }
}
